import { useReducer, useCallback, useRef, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { WalletStore, ApiQueries } from '@zen/common-app-parts';
import { useUpdateEffect } from '@zen/common-react-hooks';
import reducer, { ACTIONS } from './operationsReducer';

const REFETCH_INTERVAL = 5000;

const State = {
  pkHash: '',
  transactions: [],
  nonPendingTxCount: 0,
  executions: [],
  operations: [],
  items: [], // hold the final items for client after filtering
  filter: '',
  loading: {
    executions: false,
    transactions: false,
    items: true, // show show loading on first init
  },
};
/**
 * A store which holds user transactions and executions
 * and computes a list of operations which is a combination of the txs and executions
 * this list can be filtered, after which the operations array will hold the new result
 * DEPENDENT on the wallet
 */
export default function useOperationsStore({ dbClient } = {}) {
  const [enabled, setEnabled] = useState(false);
  const {
    state: {
      keys: { pkHash },
    },
  } = WalletStore.useStore();

  const [_s, dispatch] = useReducer(reducer, { ...State, pkHash });
  /** @type {State} */
  const state = _s;
  const subscriptionId = useRef(null);

  const fetchExecutions = useFetchExecutionsCallback({ dbClient, dispatch, pkHash });
  const { clear: clearTxsCache } = useFetchTransactions({
    dispatch,
    nonPendingTxCount: state.nonPendingTxCount,
    enabled,
  });
  const subscribeAndFetch = useSubscribeAndFetchCallback({
    dbClient,
    dispatch,
    fetchExecutions,
    pkHash,
    subscriptionId,
    setEnabled,
  });
  const unsubscribe = useUnsubscribeCallback({ dbClient, subscriptionId, setEnabled });
  const setFilter = useSetFilterCallback({ dispatch });
  const reset = useCallback(() => {
    dispatch({ type: ACTIONS.RESET, payload: { pkHash } });
    clearTxsCache();
  }, [clearTxsCache, pkHash]);

  // change handling
  useOnPkHashChange({ dispatch, pkHash });

  return {
    state,
    actions: {
      setFilter,
      reset,
      poll: {
        start: subscribeAndFetch,
        stop: unsubscribe,
      },
    },
  };
}

function useOnPkHashChange({ pkHash, dispatch } = {}) {
  // do not run on init
  useUpdateEffect(() => {
    dispatch({ type: ACTIONS.RESET, payload: { pkHash } });
  }, [dispatch, pkHash]);
}

function useFetchExecutionsCallback({ dispatch, dbClient, pkHash }) {
  return useCallback(async () => {
    dispatch({ type: ACTIONS.FETCH_EXECUTIONS.REQUESTED });
    const { items } = await dbClient.findContractExecutionsByPkHash({ pkHash });
    dispatch({ type: ACTIONS.FETCH_EXECUTIONS.SUCCEEDED, payload: items });
  }, [dbClient, dispatch, pkHash]);
}

/**
 * Fetch only the needed wallet transactions
 */
function useFetchTransactions({ nonPendingTxCount, enabled, dispatch }) {
  const {
    actions: { fetchTransactions },
  } = WalletStore.useStore();
  const { useFetchTxCount } = ApiQueries;
  const { data: count, clear: clearTxCount } = useFetchTxCount({
    refetchInterval: REFETCH_INTERVAL,
    enabled,
  });

  const take = (count || 0) - nonPendingTxCount;

  const fetchTxAndDispatch = useCallback(async () => {
    dispatch({ type: ACTIONS.FETCH_TRANSACTIONS.REQUESTED });
    try {
      return await fetchTransactions({ take });
    } catch (error) {
      dispatch({ type: ACTIONS.FETCH_TRANSACTIONS.FAILED, payload: error });
    }
  }, [dispatch, fetchTransactions, take]);
  const { data: txs, clear: clearTxs } = useQuery(
    ['fetch-all-transactions', { take }],
    fetchTxAndDispatch,
    {
      enabled: enabled && count && take > 0,
    }
  );

  useEffect(() => {
    if (txs) {
      dispatch({ type: ACTIONS.FETCH_TRANSACTIONS.SUCCEEDED, payload: txs });
    } else {
      // nothing to fetch
      dispatch({ type: ACTIONS.FETCH_TRANSACTIONS.SUCCEEDED, payload: [] });
    }
  }, [dispatch, txs]);

  const clear = useCallback(() => {
    clearTxCount();
    clearTxs();
  }, [clearTxCount, clearTxs]);

  return { clear };
}

function useSubscribeAndFetchCallback({
  subscriptionId,
  dbClient,
  pkHash,
  dispatch,
  fetchExecutions,
  setEnabled,
}) {
  return useCallback(() => {
    if (subscriptionId.current) return; // do not subscribe twice

    subscriptionId.current = dbClient.subscribe({
      method: 'findContractExecutionsByPkHash',
      payload: { pkHash },
      handler: ({ items }) => {
        dispatch({
          type: ACTIONS.FETCH_EXECUTIONS.SUCCEEDED,
          payload: items,
        });
      },
    });
    setEnabled(true);
    fetchExecutions();
  }, [dbClient, dispatch, fetchExecutions, pkHash, setEnabled, subscriptionId]);
}

function useUnsubscribeCallback({ subscriptionId, setEnabled, dbClient }) {
  return useCallback(() => {
    setEnabled(false);
    if (subscriptionId.current) {
      dbClient.unsubscribe(subscriptionId.current);
      subscriptionId.current = null;
    }
  }, [dbClient, subscriptionId, setEnabled]);
}

function useSetFilterCallback({ dispatch }) {
  return useCallback(
    (filter) => {
      dispatch({ type: ACTIONS.FILTER_CHANGED, payload: filter });
    },
    [dispatch]
  );
}
